import React, { useState, useEffect } from 'react';
import RangeSlider from 'react-slider'; // Imports the slider component for range selection
import Switch from '@mui/material/Switch'; // Imports the Switch component for boolean parameters
import './Catalog.css'; // Imports the CSS file for styling the component
import { getData } from '../service/getData'; // Imports the function to fetch module data
import '@fontsource/open-sans'; // Imports the Open Sans font
import toast from "react-hot-toast";

/**
 * Parses a range string into an object with min and max values.
 * If the input is a single value, it is returned as both min and max.
 * 
 * @param {string|number} rangeStr - The range string or number to be parsed.
 * @returns {Object} Object with min and max properties.
 */
const parseRange = (rangeStr) => {
  if (typeof rangeStr === 'string') {
    const [min, max] = rangeStr.slice(1, -1).split(';').map(Number);
    return { min, max };
  }
  return { min: rangeStr, max: rangeStr };
};

/**
 * The Catalog component displays a list of module categories and their parameters for filtering.
 * Users can select modules based on various parameters and submit them to be added to the rack.
 * 
 * @param {function} onSelectModule - Callback to handle the selection of a module to add to the rack.
 */
const Catalog = ({ onSelectModule }) => {
  // State to manage the expanded state of each category
  const [expandedCategories, setExpandedCategories] = useState({});
  // State to manage selected parameter values for filtering
  const [selectedParameters, setSelectedParameters] = useState({});
  // State to store default slider values for each parameter
  const [sliderDefaults, setSliderDefaults] = useState({});
  // State to store the checked states for boolean parameters (switches)
  const [checkedStates, setCheckedStates] = useState({});
  // State to store the fetched modules
  const [modules, setModules] = useState({});

  /**
   * Fetches module data when the component mounts and stores it in the modules state.
   */
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const data = await getData(); // Fetches module data
        setModules(data); // Updates the state with the module data
      } catch (error) {
        console.error('Error fetching modules:', error); // Handles fetch errors
      }
    };
    fetchModules(); // Calls the data fetching function
  }, []); // The empty array ensures this runs only once

  useEffect(()=>{
    Object.keys(modules).map((category, index) => (
      Object.keys(modules[category][0])
        .filter(param => !excludedParams.includes(param))
        .map((param, idx) => (
          typeof modules[category]?.[0][param] === 'boolean' ? onSelectParameter(category, param, false) : onSelectParameter(category, param, [0, getMax(modules[category], param)])
        ))
    ))
  },[modules])
  
  /**
   * Toggles the expanded state of a category to show or hide its parameters.
   * 
   * @param {string} category - The category to toggle.
   */
  const toggleCategory = (category) => {
    setExpandedCategories((prevState) => ({
      ...prevState,
      [category]: !prevState[category], // Toggles between expanded and collapsed
    }));
  };

  /**
   * Handles selecting a parameter value within a category.
   * Updates the selectedParameters state with the new min and max values.
   * 
   * @param {string} category - The category containing the parameter.
   * @param {string} parameter - The parameter being updated.
   * @param {Array<number>} values - The selected min and max values.
   */
  const onSelectParameter = (category, parameter, values) => {
    setSelectedParameters((prevState) => {
      const newCategory = { ...prevState[category] };
      let newParameter = newCategory[parameter] || {};

      newParameter.min = values[0];
      newParameter.max = values[1];

      newCategory[parameter] = newParameter;

      return {
        ...prevState,
        [category]: newCategory,
      };
    });
  };

  /**
   * Filters the modules based on the selected parameters for a given category.
   * 
   * @param {string} category - The category to filter modules.
   * @returns {Array} The filtered list of modules.
   */
  const filterModules = (category) => {
    const categoryModules = modules[category];
    const selectedParams = selectedParameters[category];

    if (!selectedParams) return categoryModules;

    return categoryModules.filter((module) =>
      Object.entries(selectedParams).every(([key, { min, max }]) => {
        const moduleRange = parseRange(module[key]);
        if (moduleRange.min === moduleRange.max) {
          if (min !== undefined && moduleRange.max > max) return false;
          if (max !== undefined && moduleRange.min < min) return false;
        }else if ((min !== undefined && moduleRange.max < max) || (max !== undefined && moduleRange.min > min)) return false;
       
        return true;
      })
    );
  };

  /**
   * Handles submitting the filtered modules for a given category.
   * Selects the best (cheapest) module and passes it to the onSelectModule callback.
   * 
   * @param {string} category - The category to filter and submit modules.
   */
  const handleSubmit = (category) => {
    const filteredModules = filterModules(category);

    if (filteredModules.length > 0) {
      const bestModule = filteredModules.reduce((prev, curr) =>
        prev['Price_€'] < curr['Price_€'] ? prev : curr
      );
      onSelectModule(bestModule); // Passes the best module to the callback
    } else {
      toast('No modules found matching the criteria.'); // Alerts if no module is found
    }
  };

  // List of parameters to exclude from filtering
  const excludedParams = ['3DFile', 'Height_U', 'Width_HP', 'PartNumber', 'Price_€', 'LeadTime_WorkingDays', 'yPos', 'subModules', 'subModuleType'];

  /**
   * Handles clearing all selected parameters, resetting sliders, switches, and states.
   */
  const handleClear = () => {
    setSelectedParameters({});
    setSliderDefaults({});
    setCheckedStates({}); 
  };

  /**
   * Formats a parameter name by replacing underscores with spaces and capitalizing words.
   * 
   * @param {string} name - The parameter name to be formatted.
   * @returns {string} The formatted parameter name.
   */
  const formatParameterName = (name) => {
    return name
      .replace(/_/g, ' ') // Replaces underscores with spaces
      .replace(/: $/, '') // Removes trailing colons
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Adds space between uppercase and lowercase letters
  };

  /**
   * Finds the maximum value for a parameter from a list of modules.
   * 
   * @param {Array} modules - The list of modules.
   * @param {string} param - The parameter to find the maximum value for.
   * @returns {number} The maximum value found.
   */
  const getMax = (m, p) => {
    let max = 0;

    m.forEach(module => {
      const str = module[p];
      if (typeof str === 'string' && str.startsWith('[')) {
        // Extracts the number after the semicolon
        const num = parseInt(str.split(';')[1].replace(']', ''), 10);

        // Updates max if the current number is larger
        if (num > max) {
          max = num;
        }
      } else if (typeof str === 'number') {
        if (str > max) {
          max = str;
        }
      }
    });
    return max;
  };

  return (
    <div style={{ width: '100%', height: 'auto' }}>
      {/* Header section with title */}
      <div style={{ width: '99.5%', backgroundColor: '#2b8cb9', padding: '1px', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
        <h2 style={{ fontFamily: 'Open Sans', color: 'white', fontSize: '24px', textAlign: 'center' }}>
          Module Catalog
        </h2>
      </div>

      {/* Main catalog container */}
      <div className="module-catalog" style={{ backgroundColor: '#EDF5FB', height: '88vh', position: 'relative' }}>
        {/* Category and parameter list */}
        <div style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column', marginTop: '-5%' }} className="module-list">
          {Object.keys(modules).map((category, index) => (
            <div key={index} className="category" style={{ backgroundColor: '#fff', alignItems: 'center', border: 'solid 1px #1976d2', borderRadius: '5px', }}>
              {/* Category header with expand option */}
              <div onClick={() => toggleCategory(category)} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h3 style={{ fontFamily: 'Open Sans', fontSize: '24px', marginBottom: '5%' }}>{category}</h3>
                <h2 style={{ color: '#399ACE' }}>{expandedCategories[category] ? '-' : '+'}</h2>
              </div>

              {/* Parameters of the category */}
              {expandedCategories[category] && (
                <div className="parameter-selection">
                  {modules[category].length > 0 &&
                    Object.keys(modules[category][0])
                      .filter(param => !excludedParams.includes(param))
                      .map((param, idx) => (
                        <div key={idx} className="parameter">
                          {/* Renders switches for boolean parameters */}
                          {typeof modules[category]?.[0][param] === 'boolean' ? (
                            <div className="parameter">
                              <label>{formatParameterName(param)}:</label>
                              <Switch
                                checked={checkedStates[`${category}-${param}`] || false}
                                onChange={(event) => {
                                  const isChecked = event.target.checked;
                                  setCheckedStates((prev) => ({
                                    ...prev,
                                    [`${category}-${param}`]: isChecked,
                                  }));
                                  onSelectParameter(category, param, [isChecked]);
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            </div>
                          ) : (
                            // Renders sliders for numeric parameters
                            <div className="parameter">
                              <label style={{ fontFamily: 'Open Sans', fontSize: '14px', marginTop: '2%', color: 'black', textAlign: 'center' }}>
                                {formatParameterName(param)}:
                              </label>
                              <div className="parameter-inputs">
                                <div className="slider-container" style={{ position: 'relative' }}>
                                  <RangeSlider
                                    className="horizontal-slider"
                                    thumbClassName="example-thumb"
                                    trackClassName="example-track"
                                    max={getMax(modules[category], param)}
                                    value={sliderDefaults[`${category}-${param}`] || [0, getMax(modules[category], param)]}
                                    onChange={(values) => {
                                      onSelectParameter(category, param, values);
                                      setSliderDefaults((prev) => ({
                                        ...prev,
                                        [`${category}-${param}`]: values,
                                      }));
                                    }}
                                  />
                                  {/* Displays slider values */}
                                  <div className="slider-values" style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'space-between', textAlign: 'center' }}>
                                    <span
                                      className="slider-value"
                                      style={{
                                        left: `${((sliderDefaults[`${category}-${param}`]?.[0] || 0) / getMax(modules[category], param)) * 100}%`,
                                        position: 'absolute',
                                        transform: 'translateX(-30%)', // Centers horizontally
                                        top: '-70px', // Adjusts vertical position above the cursor
                                        whiteSpace: 'nowrap', // Prevents line break
                                        textAlign: 'center', // Centers the text
                                      }}
                                    >
                                      {sliderDefaults[`${category}-${param}`]?.[0] || 0}
                                    </span>
                                    <span
                                      className="slider-value"
                                      style={{
                                        left: `${((sliderDefaults[`${category}-${param}`]?.[1] || getMax(modules[category], param)) / getMax(modules[category], param)) * 100}%`,
                                        position: 'absolute',
                                        transform: 'translateX(-60%)', // Centers horizontally
                                        bottom: '100%',
                                        whiteSpace: 'nowrap', // Prevents line break
                                        textAlign: 'center', // Centers the text
                                      }}
                                    >
                                      {sliderDefaults[`${category}-${param}`]?.[1] || getMax(modules[category], param)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  {/* Button to add modules */}
                  <button
                    style={{
                      borderRadius: '5px', marginTop: '10%', padding: '15px', width: '100%', backgroundColor: '#fff', border: '1px solid #1976d2', color: '#1976d2', textTransform: 'none', fontSize: '16px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2',
                      }
                    }}
                    onClick={() => handleSubmit(category)}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Button to clear all filters */}
        <button
          style={{
            borderRadius: '5px', marginTop: '10%', padding: '15px 10px', width: '100%', backgroundColor: '#fff', border: '1px solid #1976d2', color: '#1976d2', textTransform: 'none', fontSize: '16px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#1976d2',
            }
          }}
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default Catalog;
